import {
    Hero,
    MobileSticky,
    TabletSticky,
    GameThemeProvider,
    usePaginatedResult,
    RoundNavigator,
    WhoTippedWhatSkeleton,
    PaginatorControlsSkeleton,
    Button,
} from '@the-game/components'
import { Content } from '@the-game/components'
import React from 'react'
import { AdUnitWrapper, usePageTargeting } from '@the-game/gpt'
import { PageHelmet } from '../../meta/get-meta'
import { NotFound } from '../../notfound'
import {
    ExpertTipsDTO,
    ExpertTipsMember,
    SportName,
} from '@the-game/api-interfaces'
import { useUser } from '../../authentication/UserContext'
import { StyledMessage } from './who-tipped-what'
import { useAvailableGame } from '../../data/useAvailableGame'
import { GameContext, useGameContext } from '@the-game/web-common'
import ExpertTips from '@the-game/components/ExpertTips'
import { useExpertTips } from '../../data/useExpertTips'
import { StyledHeader, StyledStack, TipsStyledStack } from './view.styled'
import { useExpertTipsNextRound } from '../../data/useExpertTipsNextRound'

interface ViewExpertCompetitionProps {
    sport: SportName
    topAds: JSX.Element
    bottomAds?: JSX.Element
    userSub: string
}

function ViewExpertCompetition({
    sport,
    topAds,
    bottomAds,
}: ViewExpertCompetitionProps) {
    const gameContext = useAvailableGame({
        sport,
        game: 'Tipping',
    })

    usePageTargeting(`${sport}/experttips`)

    if (!gameContext) {
        return <NotFound />
    }

    return (
        <GameContext.Provider value={gameContext}>
            <GameThemeProvider sport={gameContext.sport}>
                <Content layout="full">
                    <PageHelmet page="expert-tips" />
                    <Hero heading="Expert Tips">
                        <p>
                            Check here each week to see what the experts are
                            tipping!
                        </p>
                    </Hero>
                    {topAds}

                    {gameContext.totalRounds >= 1 && <ExpertTipsWrapper />}

                    {bottomAds}
                </Content>
            </GameThemeProvider>
        </GameContext.Provider>
    )
}

function Wrapper({
    highestOpenRound,
    round,
    setRound,
    data,
    sport,
    children,
}: {
    highestOpenRound: number
    round: number | undefined
    setRound: (val: number | undefined) => void
    data: ExpertTipsDTO | undefined
    sport: SportName
    children: React.ReactNode
}) {
    return (
        <StyledStack direction="column" gap="sm">
            <StyledHeader>Tips</StyledHeader>
            <RoundNavigator
                totalRounds={highestOpenRound}
                value={round ?? (data && data.roundNumber)}
                onChange={(value) => setRound(value)}
                sport={sport}
            />
            {children}
        </StyledStack>
    )
}

export function ExpertTipsWrapper() {
    const [tippedPage, setTippedPage] = React.useState<number>(1)
    const { sport, season, currentRound, totalRounds } = useGameContext()
    const [round, setRound] = React.useState<number | undefined>(currentRound)
    const queryResult = useExpertTips(sport, season, round)

    const queryResultNext = useExpertTipsNextRound(
        sport,
        season,
        currentRound,
        totalRounds,
    )

    const {
        data,
        canFetchMore,
        fetchMore,
        isError,
        isLoading,
        isPreviousData,
    } = queryResult
    const { data: nextRoundData } = queryResultNext

    const hasNextRound = !!nextRoundData?.page.some((page) => {
        return page.tips.some((tip) => tip === 'HOME' || tip === 'AWAY')
    })
    const highestOpenRound = hasNextRound ? currentRound + 1 : currentRound

    const { pagination } = usePaginatedResult<ExpertTipsMember, ExpertTipsDTO>({
        pageNumber: tippedPage,
        queryResult: queryResult,
        pageSize: 50,
    })

    if (!isPreviousData && isLoading) {
        return (
            <TipsStyledStack direction="column" gap="sm">
                <StyledHeader>Tips</StyledHeader>
                <RoundNavigator
                    totalRounds={currentRound}
                    value={round}
                    onChange={(value) => setRound(value)}
                    sport={sport}
                />
                <WhoTippedWhatSkeleton rows={Math.min(10, 1) + 1} />
                <PaginatorControlsSkeleton />
            </TipsStyledStack>
        )
    }

    if (isError) {
        return (
            <Wrapper
                highestOpenRound={highestOpenRound}
                round={round}
                setRound={setRound}
                data={data}
                sport={sport}
            >
                <StyledMessage>Something went wrong</StyledMessage>
            </Wrapper>
        )
    }

    if (data) {
        return (
            <Wrapper
                highestOpenRound={highestOpenRound}
                round={round}
                setRound={setRound}
                data={data}
                sport={sport}
            >
                <ExpertTips
                    matches={data.matches}
                    pagination={pagination}
                    goToPage={setTippedPage}
                    userColumnLabel={'Name'}
                />
                {canFetchMore && (
                    <Button onClick={() => fetchMore()}>Load More</Button>
                )}
            </Wrapper>
        )
    } else {
        return (
            <Wrapper
                highestOpenRound={highestOpenRound}
                round={round}
                sport={sport}
                setRound={setRound}
                data={data}
            >
                <StyledMessage>No data available</StyledMessage>
            </Wrapper>
        )
    }
}

export function ViewExpertTippersCompetition({ sport }: { sport: SportName }) {
    const { oidcUser } = useUser()

    const userSub = oidcUser?.profile['swm:identifier']
    if (!userSub) {
        throw new Error(
            'A logged in user on the competition page should not be missing a profile and subject.',
        )
    }

    return (
        <ViewExpertCompetition
            sport={sport}
            userSub={userSub}
            topAds={
                <>
                    <AdUnitWrapper
                        definition={{
                            id: 'skins',
                            size: 'skins',
                        }}
                        noticePosition="none"
                        onEvent={() => undefined}
                    />
                    <AdUnitWrapper
                        definition={{
                            id: 'competition-leaderboard-leaderboard',
                            size: 'leaderboard',
                        }}
                        noticePosition="none"
                        onEvent={() => undefined}
                    />
                    <AdUnitWrapper
                        definition={{
                            id: 'competition-leaderboard',
                            size: 'tabletLeaderboard',
                        }}
                        noticePosition="none"
                        onEvent={() => undefined}
                    />
                    <AdUnitWrapper
                        definition={{
                            id: 'competition-tips-mobile-leaderboard',
                            size: 'mobileLeaderboard',
                        }}
                        noticePosition="none"
                        onEvent={() => undefined}
                    />
                    <MobileSticky>
                        <AdUnitWrapper
                            definition={{
                                id: 'competition-mobile-leaderboard-sticky',
                                size: 'mobileLeaderboard',
                            }}
                            noticePosition="none"
                            onEvent={() => undefined}
                        />
                    </MobileSticky>
                    <TabletSticky>
                        <AdUnitWrapper
                            definition={{
                                id: 'competition-tablet-leaderboard-sticky',
                                size: 'tabletLeaderboard',
                            }}
                            noticePosition="none"
                            onEvent={() => undefined}
                        />
                    </TabletSticky>
                </>
            }
            bottomAds={
                <AdUnitWrapper
                    definition={{
                        id: 'competition-lower-leaderboard',
                        size: 'leaderboard',
                    }}
                    noticePosition="none"
                    onEvent={() => undefined}
                />
            }
        />
    )
}

export default ViewExpertTippersCompetition
